<template>
  <div class="stats-charts">
    <div class="chart-card admin-card">
      <h3 class="stats-card-title">Accuracy over time</h3>
      <p class="admin-hint">Percentage of cards placed in the correct position, per game.</p>
      <div class="chart-canvas">
        <Line :data="accuracyData" :options="percentOptions" />
      </div>
    </div>

    <div class="chart-card admin-card">
      <h3 class="stats-card-title">Average time per deal</h3>
      <p class="admin-hint">Seconds from cards being dealt to pressing "I'm Done".</p>
      <div class="chart-canvas">
        <Line :data="dealTimeData" :options="secondsOptions" />
      </div>
    </div>

    <div class="chart-card admin-card">
      <h3 class="stats-card-title">Average mistakes over time</h3>
      <p class="admin-hint">Running average of cards out of position per game, smoothing out one-off bad rounds.</p>
      <div class="chart-canvas">
        <Line :data="mistakesData" :options="mistakesOptions" />
      </div>
    </div>

    <div class="chart-card admin-card">
      <h3 class="stats-card-title">Cards dealt per game</h3>
      <p class="admin-hint">Total cards turned over, opening hand plus every replacement.</p>
      <div class="chart-canvas">
        <Line :data="cardsDealtData" :options="countOptions" />
      </div>
    </div>

    <div class="chart-card admin-card">
      <h3 class="stats-card-title">Longest streak</h3>
      <p class="admin-hint">Most cards placed correctly in a row before a mistake, carried across deals.</p>
      <div class="chart-figure">
        <span class="chart-figure__value">{{ longestStreak }}</span>
        <span class="chart-figure__unit">cards</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);
ChartJS.defaults.font.family = 'proxima-nova, sans-serif';

const props = defineProps({
  sessions: {
    type: Array,
    default: () => [],
  },
});

// Brand palette, avoiding blue and purple fills.
const GREEN = '#00a63d';
const ORANGE = '#ff6800';
const RED = '#F60F01';
const AMBER = '#ffb300';
const SLATE = '#6b7280';
const GRID = '#f0f1f3'; // --color-border-soft

const labels = computed(() => props.sessions.map((_, i) => `Game ${i + 1}`));

const lineDataset = (label, data, color) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color,
  pointBackgroundColor: color,
  pointRadius: 4,
  pointHoverRadius: 6,
  borderWidth: 2,
  tension: 0.3,
  fill: false,
});

const accuracyData = computed(() => ({
  labels: labels.value,
  datasets: [
    lineDataset(
      'Correct placements',
      props.sessions.map((s) =>
        s.cards_total ? Math.round((s.cards_correct / s.cards_total) * 1000) / 10 : 0
      ),
      GREEN
    ),
  ],
}));

const dealTimeData = computed(() => ({
  labels: labels.value,
  datasets: [
    lineDataset(
      'Seconds per deal',
      props.sessions.map((s) => Math.round((s.avg_deal_ms || 0) / 100) / 10),
      ORANGE
    ),
  ],
}));

// Running average so the trend is readable rather than spiky game to game.
const runningAverageMistakes = computed(() => {
  let total = 0;
  return props.sessions.map((s, i) => {
    total += s.mistakes || 0;
    return Math.round((total / (i + 1)) * 10) / 10;
  });
});

const mistakesData = computed(() => ({
  labels: labels.value,
  datasets: [
    lineDataset('Average mistakes', runningAverageMistakes.value, RED),
    lineDataset(
      'Mistakes this game',
      props.sessions.map((s) => s.mistakes || 0),
      SLATE
    ),
  ],
}));

const cardsDealtData = computed(() => ({
  labels: labels.value,
  datasets: [
    lineDataset(
      'Cards dealt',
      props.sessions.map((s) => s.cards_dealt || 0),
      AMBER
    ),
  ],
}));

const longestStreak = computed(() =>
  props.sessions.reduce((best, s) => Math.max(best, s.longest_streak || 0), 0)
);

const legendHidden = {
  display: false,
};

const legendVisible = {
  display: true,
  position: 'bottom',
  labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } },
};

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    legend: legendHidden,
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, grid: { color: GRID } },
  },
};

const percentOptions = {
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    y: {
      ...baseOptions.scales.y,
      max: 100,
      ticks: { callback: (v) => `${v}%` },
    },
  },
};

const secondsOptions = {
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    y: { ...baseOptions.scales.y, ticks: { callback: (v) => `${v}s` } },
  },
};

const countOptions = { ...baseOptions };

// Mistakes chart is the only multi-series chart, so it keeps a legend.
const mistakesOptions = {
  ...baseOptions,
  plugins: {
    legend: legendVisible,
  },
};
</script>
